/**
 * InservicesSection — Onboarding Kit: library of staff in-service trainings.
 * Which ones an employee must take is chosen per employee when their orientation link is created.
 */
import { useRef, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import FillableFormEditor from "@/components/FillableFormEditor";
import InservicePdfBlock, { uploadInservicePdf } from "@/components/InservicePdfBlock";
import { FileText, GraduationCap, Link2, Loader2, Trash2, Upload } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function InservicesSection({ kit, setKit, readonly }) {
  const items = kit.inservices || [];
  const fileRef = useRef(null);
  const pdfRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [uploadingPdf, setUploadingPdf] = useState(false);
  const [pdfEditorIdx, setPdfEditorIdx] = useState(null);

  const update = (idx, patch) => setKit((k) => { const inservices = [...(k.inservices || [])]; inservices[idx] = { ...inservices[idx], ...patch }; return { ...k, inservices }; });
  const remove = (idx) => setKit((k) => ({ ...k, inservices: (k.inservices || []).filter((_, i) => i !== idx) }));
  const add = (item) => setKit((k) => ({ ...k, inservices: [...(k.inservices || []), { title: "New in-service", description: "", required: true, hours: 1, order: (k.inservices || []).length, ...item }] }));

  const upload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const form = new FormData();
      form.append("file", file);
      const res = await axios.post(`${API}/settings/orientation-kit/upload-video`, form, { headers: { "Content-Type": "multipart/form-data" } });
      add({ title: file.name.replace(/\.[^.]+$/, ""), source_type: "upload", storage_path: res.data.storage_path, content_type: res.data.content_type });
      toast.success("In-service video uploaded — remember to Save the kit");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Upload failed");
    } finally {
      setUploading(false);
      e.target.value = "";
    }
  };

  const uploadPdf = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploadingPdf(true);
    try {
      const patch = await uploadInservicePdf(file);
      add({ title: file.name.replace(/\.[^.]+$/, ""), source_type: "pdf", ...patch });
      toast.success(patch.pdf_fillable_form ? `PDF in-service added — ${patch.pdf_fillable_form.fields.length} fields detected` : "PDF in-service added — use Edit fields to add fill-in boxes");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Upload failed");
    } finally {
      setUploadingPdf(false);
      e.target.value = "";
    }
  };

  const editing = pdfEditorIdx !== null ? items[pdfEditorIdx] : null;

  return (
    <div className="bg-white rounded-xl border border-slate-200 p-6" data-testid="inservices-section">
      <div className="flex items-center justify-between mb-1">
        <div className="flex items-center gap-2">
          <GraduationCap className="w-5 h-5 text-teal-600" />
          <h3 className="font-semibold text-slate-900">In-service trainings</h3>
          <span className="text-xs text-slate-500">({items.length})</span>
        </div>
        {!readonly && (
          <div className="flex gap-2">
            <Button variant="outline" size="sm" onClick={() => add({ source_type: "url", url: "" })} className="gap-1" data-testid="add-inservice-url-btn"><Link2 className="w-4 h-4" /> Add from link</Button>
            <Button variant="outline" size="sm" onClick={() => fileRef.current?.click()} disabled={uploading} className="gap-1" data-testid="add-inservice-upload-btn">
              {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />} Upload video
            </Button>
            <input ref={fileRef} type="file" accept="video/*" className="hidden" onChange={upload} />
            <Button variant="outline" size="sm" onClick={() => pdfRef.current?.click()} disabled={uploadingPdf} className="gap-1" data-testid="add-inservice-pdf-btn">
              {uploadingPdf ? <Loader2 className="w-4 h-4 animate-spin" /> : <FileText className="w-4 h-4" />} Upload PDF
            </Button>
            <input ref={pdfRef} type="file" accept="application/pdf" className="hidden" onChange={uploadPdf} />
          </div>
        )}
      </div>
      <p className="text-xs text-slate-500 mb-4">Build the library here. When you create an employee's orientation link, tick the in-services they must take — they open in the same link and count toward completion. An in-service can be a video, a PDF the employee fills out and signs, or both; completed PDFs are filed to the employee's HR file automatically.</p>
      {items.length === 0 ? (
        <p className="text-sm text-slate-500 italic text-center py-6">No in-services yet — add a YouTube / Vimeo link, upload a video or upload a fillable PDF.</p>
      ) : (
        <div className="space-y-3">
          {items.map((it, idx) => (
            <div key={it.id || idx} className="rounded-lg border border-slate-200 p-3 space-y-2" data-testid={`inservice-row-${idx}`}>
              <div className="grid gap-2 sm:grid-cols-[1fr_110px_auto] items-end">
                <div>
                  <Label className="text-xs">Title</Label>
                  <Input value={it.title} onChange={(e) => update(idx, { title: e.target.value })} disabled={readonly} data-testid={`inservice-title-${idx}`} />
                </div>
                <div>
                  <Label className="text-xs">Hours (CEU)</Label>
                  <Input type="number" step="0.25" min="0" value={it.hours ?? ""} onChange={(e) => update(idx, { hours: e.target.value === "" ? null : Number(e.target.value) })} disabled={readonly} data-testid={`inservice-hours-${idx}`} />
                </div>
                {!readonly && (
                  <Button variant="ghost" size="sm" onClick={() => remove(idx)} className="text-rose-600" data-testid={`inservice-remove-${idx}`}><Trash2 className="w-4 h-4" /></Button>
                )}
              </div>
              {it.source_type === "url" && (
                <div>
                  <Label className="text-xs">Video link (YouTube, Vimeo or direct MP4)</Label>
                  <Input value={it.url || ""} onChange={(e) => update(idx, { url: e.target.value })} placeholder="https://…" disabled={readonly} data-testid={`inservice-url-${idx}`} />
                </div>
              )}
              {it.source_type === "upload" && <p className="text-xs text-slate-500">Uploaded video{it.content_type ? ` · ${it.content_type}` : ""}</p>}
              {it.source_type === "pdf" && <p className="text-xs text-slate-500">PDF in-service — the employee reads, fills out and signs the document below.</p>}
              <InservicePdfBlock item={it} idx={idx} readonly={readonly} onChange={(patch) => update(idx, patch)} onEditFields={() => setPdfEditorIdx(idx)} />
              <div>
                <Label className="text-xs">Description (optional)</Label>
                <Textarea value={it.description || ""} onChange={(e) => update(idx, { description: e.target.value })} rows={2} disabled={readonly} />
              </div>
              <label className="flex items-center gap-2 text-sm text-slate-700">
                <Switch checked={!!it.required} onCheckedChange={(v) => update(idx, { required: v })} disabled={readonly} />
                Required when assigned
              </label>
            </div>
          ))}
        </div>
      )}

      {editing?.pdf_storage_path && (
        <FillableFormEditor
          open
          onClose={() => setPdfEditorIdx(null)}
          file={{ id: editing.id || `inservice-${pdfEditorIdx}`, filename: editing.pdf_filename || editing.title, fillable_form: editing.pdf_fillable_form }}
          endpoints={{
            pdf: `${API}/settings/orientation-kit/document-file?storage_path=${encodeURIComponent(editing.pdf_storage_path)}`,
            info: `${API}/settings/orientation-kit/document-info?storage_path=${encodeURIComponent(editing.pdf_storage_path)}`,
            detect: `${API}/settings/orientation-kit/document-detect`,
            detectBody: { storage_path: editing.pdf_storage_path },
          }}
          onSaveFields={async (fields, info) => {
            update(pdfEditorIdx, { pdf_fillable_form: { enabled: true, auto_detected: false, page_count: info.page_count, page_sizes: info.page_sizes, fields } });
            toast.info("Remember to click “Save Kit” to publish the changes");
          }}
          onClearFields={async () => update(pdfEditorIdx, { pdf_fillable_form: null })}
        />
      )}
    </div>
  );
}
